import React, { useCallback, useMemo, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import {
  useCreateGHADiscountRule,
  useGetGHADiscountRules,
  useSetGHADiscountRuleEnabled,
  useUpdateGHADiscountRule,
} from '~/hooks/apis/connection-google/useGHADiscountRules';

import {
  CreateGHADiscountRuleBody,
  GHAAccountType,
  GHADiscountRule,
  GHAProperty,
  UpdateGHADiscountRuleBody,
} from '~/services/ConnectionGoogleService';

import GHADiscountRuleDialog from './GHADiscountRuleDialog';
import { formatDateRange, formatDateTime, formatDiscountAccountType, formatRuleDiscount } from './utils';

interface Props {
  open: boolean;
  property: GHAProperty | null;
  accountTypes: Array<GHAAccountType>;
  selectedAccountType?: GHAAccountType;
  onClose: () => void;
  onRulesChanged: () => Promise<void>;
}

function getColumns(
  onEdit: (rule: GHADiscountRule) => void,
  onToggleEnabled: (rule: GHADiscountRule) => void,
): Array<GridColDef<GHADiscountRule>> {
  return [
    {
      field: 'accountType',
      headerName: 'Account Type',
      width: 160,
      valueGetter: (value) => formatDiscountAccountType(value),
    },
    {
      field: 'discountValue',
      headerName: 'Discount',
      width: 160,
      valueGetter: (_value, row) => formatRuleDiscount(row),
    },
    {
      field: 'minAdvanceBookingWindow',
      headerName: 'Min ABW',
      width: 110,
    },
    {
      field: 'minLengthOfStay',
      headerName: 'Min LOS',
      width: 110,
    },
    {
      field: 'enabled',
      headerName: 'Status',
      width: 120,
      renderCell: ({ row }) => (
        <Chip label={row.enabled ? 'Enabled' : 'Disabled'} color={row.enabled ? 'success' : 'default'} size="small" />
      ),
    },
    {
      field: 'startsAt',
      headerName: 'Active Period',
      width: 300,
      valueGetter: (_value, row) => formatDateRange(row),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 170,
      valueGetter: (value) => formatDateTime(value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ row }) => [
        <GridActionsCellItem key="edit" icon={<EditIcon />} label="Edit" onClick={() => onEdit(row)} showInMenu />,
        <GridActionsCellItem
          key="toggle"
          label={row.enabled ? 'Disable' : 'Enable'}
          onClick={() => onToggleEnabled(row)}
          showInMenu
        />,
      ],
    },
  ];
}

function GHAPropertyDiscountRulesDialog({
  open,
  property,
  accountTypes,
  selectedAccountType,
  onClose,
  onRulesChanged,
}: Props) {
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editableRule, setEditableRule] = useState<GHADiscountRule | null>(null);
  const allowedAccountTypes = useMemo(
    () => (selectedAccountType ? [selectedAccountType] : accountTypes),
    [accountTypes, selectedAccountType],
  );
  const accountTypeOptions = useMemo(
    () =>
      allowedAccountTypes.map((value) => ({
        value: value,
        label: formatDiscountAccountType(value),
      })),
    [allowedAccountTypes],
  );
  const filters = useMemo(
    () => ({
      propertyId: property?.id,
      accountType: selectedAccountType,
    }),
    [property?.id, selectedAccountType],
  );

  const {
    data: fetchedRules = [],
    isLoading,
    isError,
    error,
  } = useGetGHADiscountRules(filters, {
    enabled: open && !!property?.id,
  });
  const rules = useMemo(
    () => fetchedRules.filter((rule) => allowedAccountTypes.includes(rule.accountType)),
    [allowedAccountTypes, fetchedRules],
  );
  const createRuleMutation = useCreateGHADiscountRule();
  const updateRuleMutation = useUpdateGHADiscountRule();
  const setEnabledMutation = useSetGHADiscountRuleEnabled();
  const isSaving = createRuleMutation.isPending || updateRuleMutation.isPending;

  const handleOpenCreateDialog = () => {
    setEditableRule(null);
    setRuleDialogOpen(true);
  };

  const handleEdit = useCallback((rule: GHADiscountRule) => {
    setEditableRule(rule);
    setRuleDialogOpen(true);
  }, []);

  const handleClose = () => {
    setRuleDialogOpen(false);
    setEditableRule(null);
    onClose();
  };

  const handleCreate = async (input: CreateGHADiscountRuleBody) => {
    try {
      await createRuleMutation.mutateAsync(input);
      setRuleDialogOpen(false);
      setEditableRule(null);
      await onRulesChanged();
    } catch {
      // Mutation hooks show the snackbar; keep the dialog open for correction or retry.
    }
  };

  const handleUpdate = async (input: UpdateGHADiscountRuleBody) => {
    if (!editableRule) {
      return;
    }

    try {
      await updateRuleMutation.mutateAsync({ id: editableRule.id, input });
      setRuleDialogOpen(false);
      setEditableRule(null);
      await onRulesChanged();
    } catch {
      // Mutation hooks show the snackbar; keep the dialog open for correction or retry.
    }
  };

  const handleToggleEnabled = useCallback(
    async (rule: GHADiscountRule) => {
      try {
        await setEnabledMutation.mutateAsync({ id: rule.id, enabled: !rule.enabled });
        await onRulesChanged();
      } catch {
        // Mutation hook shows the snackbar.
      }
    },
    [onRulesChanged, setEnabledMutation],
  );

  const columns = useMemo(() => getColumns(handleEdit, handleToggleEnabled), [handleEdit, handleToggleEnabled]);
  const canCreateRule = property !== null && accountTypeOptions.length > 0;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>GHA Discount Rules</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1">{property?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {property?.id}
              </Typography>
            </Box>
            {accountTypeOptions.length === 0 && (
              <Alert severity="warning">This property has no account type for the current brand context.</Alert>
            )}
            {isError && (
              <Alert severity="error">
                {error instanceof Error ? error.message : 'Failed to fetch GHA discount rules'}
              </Alert>
            )}
            <DataGrid
              rows={rules}
              columns={columns}
              loading={isLoading || setEnabledMutation.isPending}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ pagination: GridPagination }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleClose}>
            Close
          </Button>
          <Button variant="contained" onClick={handleOpenCreateDialog} disabled={!canCreateRule}>
            Create Rule
          </Button>
        </DialogActions>
      </Dialog>
      <GHADiscountRuleDialog
        open={ruleDialogOpen}
        rule={editableRule}
        initialPropertyId={property?.id}
        lockPropertyId
        accountTypeOptions={accountTypeOptions}
        defaultAccountType={accountTypeOptions[0]?.value}
        isSaving={isSaving}
        onClose={() => setRuleDialogOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />
    </>
  );
}

export default GHAPropertyDiscountRulesDialog;
